"use client";

import { useFlow, defineFlow } from "@ui/hooks/useFlow";
import { GetCallView, VerifyPhoneView, CallScheduledView } from "./views";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useRequestCallMutation } from "./useRequestCallMutation";

const getCallFlowConfig = defineFlow({
  data: {
    name: undefined as string | undefined,
    phone: undefined as string | undefined,
    consent: undefined as boolean | undefined,
    verification_code: undefined as string | undefined,
  },
  steps: {
    request_phone_call: {
      id: "request_phone_call",
      next: () => "verify_phone",
      stepIndex: () => 1,
    },
    verify_phone: {
      id: "verify_phone",
      next: () => "call_scheduled",
      stepIndex: () => 2,
    },
    call_scheduled: {
      id: "call_scheduled",
      next: () => null,
      stepIndex: () => 3,
    },
  },
  totalSteps: () => 3,
});

export interface GetCallFlowProps {
  agentId: string;
  onClose: () => void;
  className?: string;
}

export function GetCallFlow({ agentId, onClose, className }: GetCallFlowProps) {
  const flow = useFlow(getCallFlowConfig);
  const requestCallMutation = useRequestCallMutation();

  const motionProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15 },
  };

  const classes = "tw-size-full tw-flex-1 tw-flex tw-flex-col";

  const renderStep = () => {
    switch (flow.currentStepId) {
      case "request_phone_call":
        return (
          <m.div key='request_phone_call' {...motionProps} className={classes}>
            <GetCallView
              onSubmit={data => {
                flow.setData(data);
                flow.next();
              }}
              onCancel={() => {
                captureEvent(EventName.mw_hero_agent_request_call_cancelled, { agent_id: agentId });
                onClose();
              }}
              currentStep={flow.stepIndex!}
              totalSteps={flow.totalSteps!}
              requestCallMutation={requestCallMutation}
              agentId={agentId}
            />
          </m.div>
        );
      case "verify_phone":
        return (
          <m.div key='verify_phone' {...motionProps} className={classes}>
            <VerifyPhoneView
              onSubmit={code => {
                flow.setData({ verification_code: code });
                flow.next();
              }}
              onBack={() => flow.back()}
              onResend={async () => {
                if (flow.data.phone) {
                  await requestCallMutation.mutateAsync({ phone_number: flow.data.phone });
                }
              }}
              agentId={agentId}
              phoneNumber={flow.data.phone ?? ""}
              currentStep={flow.stepIndex!}
              totalSteps={flow.totalSteps!}
            />
          </m.div>
        );
      case "call_scheduled":
        return (
          <m.div key='call_scheduled' {...motionProps} className={classes}>
            <CallScheduledView
              onClose={() => {
                captureEvent(EventName.mw_hero_agent_call_scheduled_closed, { agent_id: agentId });
                onClose();
              }}
              onResend={async () => {
                captureEvent(EventName.mw_hero_agent_retry_call_requested, { agent_id: agentId });
                flow.goToStep("request_phone_call");
              }}
              currentStep={flow.stepIndex!}
              totalSteps={flow.totalSteps!}
            />
          </m.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={className}>
      <AnimatePresence mode='wait' initial={false}>
        {renderStep()}
      </AnimatePresence>
    </div>
  );
}
